import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, BookOpen, ClipboardCheck, Video, Upload, ChevronDown, ChevronUp, X } from 'lucide-react';
import { lmsApi, assessmentApi, submissionApi, categoryApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function ManageLMS() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('courses');

  const tabs = [
    { id: 'courses', label: 'Study Materials', icon: BookOpen },
    { id: 'assessments', label: 'Assessments', icon: ClipboardCheck },
    { id: 'live-sessions', label: 'Live Sessions', icon: Video },
    { id: 'submissions', label: 'Submissions', icon: Upload },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Manage LMS</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex space-x-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-1.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'courses' && <CoursesTab />}
      {activeTab === 'assessments' && <AssessmentsTab />}
      {activeTab === 'live-sessions' && <LiveSessionsTab />}
      {activeTab === 'submissions' && <SubmissionsTab />}
    </div>
  );
}

// ========================
// COURSES TAB
// ========================
function CoursesTab() {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonCourseId, setLessonCourseId] = useState(null);

  // Course form
  const [form, setForm] = useState({ title: '', description: '', thumbnail_url: '', category_id: '', is_published: false, sort_order: 0 });

  // Lesson form
  const [lessonForm, setLessonForm] = useState({ title: '', description: '', youtube_url: '', duration_seconds: 0, is_published: false, sort_order: 0 });

  // Categories
  const [categories, setCategories] = useState([]);

  useEffect(() => { loadCourses(); loadCategories(); }, []);

  const loadCategories = async () => {
    try {
      const res = await categoryApi.listAll();
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const loadCourses = async () => {
    try {
      const res = await lmsApi.getCourses();
      setCourses(res.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally { setIsLoading(false); }
  };

  const handleSaveCourse = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      if (editingCourse) {
        await lmsApi.updateCourse(editingCourse.id, form);
        toast.success('Course updated');
      } else {
        await lmsApi.createCourse(form);
        toast.success('Course created');
      }
      setShowModal(false);
      setEditingCourse(null);
      loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save course');
    }
  };

  const handleDeleteCourse = async (id) => {
    if (!confirm('Delete this course?')) return;
    try {
      await lmsApi.deleteCourse(id);
      toast.success('Course deleted');
      loadCourses();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEditCourse = (course) => {
    setEditingCourse(course);
    setForm({
      title: course.title,
      description: course.description || '',
      thumbnail_url: course.thumbnail_url || '',
      category_id: course.category_id || '',
      is_published: course.is_published,
      sort_order: course.sort_order || 0
    });
    setShowModal(true);
  };

  const openNewCourse = () => {
    setEditingCourse(null);
    setForm({ title: '', description: '', thumbnail_url: '', category_id: '', is_published: false, sort_order: 0 });
    setShowModal(true);
  };

  const handleSaveLesson = async () => {
    if (!lessonForm.title.trim()) { toast.error('Title required'); return; }
    try {
      if (editingLesson) {
        await lmsApi.updateLesson(editingLesson.id, lessonForm);
        toast.success('Lesson updated');
      } else {
        await lmsApi.createLesson({ ...lessonForm, course_id: lessonCourseId });
        toast.success('Lesson created');
      }
      setShowLessonModal(false);
      setEditingLesson(null);
      loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save lesson');
    }
  };

  const handleDeleteLesson = async (id) => {
    if (!confirm('Delete this lesson?')) return;
    try {
      await lmsApi.deleteLesson(id);
      toast.success('Lesson deleted');
      loadCourses();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openNewLesson = (courseId) => {
    setLessonCourseId(courseId);
    setEditingLesson(null);
    setLessonForm({ title: '', description: '', youtube_url: '', duration_seconds: 0, is_published: false, sort_order: 0 });
    setShowLessonModal(true);
  };

  const openEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setLessonForm({
      title: lesson.title,
      description: lesson.description || '',
      youtube_url: lesson.youtube_url || '',
      duration_seconds: lesson.duration_seconds || 0,
      is_published: lesson.is_published,
      sort_order: lesson.sort_order || 0
    });
    setShowLessonModal(true);
  };

  if (isLoading) return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={openNewCourse} className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <Plus className="w-4 h-4 mr-2" /> New Course
        </button>
      </div>

      {courses.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No courses yet. Create your first course.</p>
      ) : (
        <div className="space-y-3">
          {courses.map(course => (
            <div key={course.id} className="bg-white border border-gray-200 rounded-lg">
              <div
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
              >
                <div className="flex items-center space-x-3">
                  {expandedCourse === course.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  <div>
                    <h3 className="font-medium text-gray-900">{course.title}</h3>
                    <div className="flex items-center space-x-2 mt-0.5">
                      <span className="text-xs text-gray-400">{course.lesson_count} lessons</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${course.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {course.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2" onClick={e => e.stopPropagation()}>
                  <button onClick={() => openEditCourse(course)} className="p-1.5 text-gray-400 hover:text-primary-600"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDeleteCourse(course.id)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              {expandedCourse === course.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Lessons</h4>
                    <button onClick={() => openNewLesson(course.id)} className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Lesson
                    </button>
                  </div>
                  {course.lessons?.length > 0 ? (
                    <div className="space-y-2">
                      {course.lessons.map((lesson, i) => (
                        <div key={lesson.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                          <div className="flex items-center space-x-3">
                            <span className="text-xs text-gray-400 w-6">{i + 1}.</span>
                            <div>
                              <p className="text-sm font-medium text-gray-800">{lesson.title}</p>
                              <div className="flex items-center space-x-2 mt-0.5">
                                {lesson.youtube_video_id && <span className="text-xs text-gray-400">Video</span>}
                                <span className={`text-xs ${lesson.is_published ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {lesson.is_published ? 'Published' : 'Draft'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button onClick={() => openEditLesson(lesson)} className="p-1 text-gray-400 hover:text-primary-600"><Edit2 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handleDeleteLesson(lesson.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">No lessons yet</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Course Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingCourse ? 'Edit Course' : 'New Course'}</h3>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail URL</label>
                <input type="text" value={form.thumbnail_url} onChange={e => setForm({ ...form, thumbnail_url: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exam Category</label>
                <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">-- Select Category --</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })} />
                <span className="text-sm font-medium text-gray-700">Published</span>
              </label>
            </div>
            <div className="flex space-x-2 mt-4">
              <button onClick={handleSaveCourse} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Modal */}
      {showLessonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingLesson ? 'Edit Lesson' : 'New Lesson'}</h3>
              <button onClick={() => setShowLessonModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" value={lessonForm.title} onChange={e => setLessonForm({ ...lessonForm, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={lessonForm.description} onChange={e => setLessonForm({ ...lessonForm, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube URL</label>
                <input type="text" value={lessonForm.youtube_url} onChange={e => setLessonForm({ ...lessonForm, youtube_url: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="https://youtube.com/watch?v=..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (seconds)</label>
                <input type="number" value={lessonForm.duration_seconds} onChange={e => setLessonForm({ ...lessonForm, duration_seconds: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input type="number" value={lessonForm.sort_order} onChange={e => setLessonForm({ ...lessonForm, sort_order: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked={lessonForm.is_published} onChange={e => setLessonForm({ ...lessonForm, is_published: e.target.checked })} />
                <span className="text-sm font-medium text-gray-700">Published</span>
              </label>
            </div>
            <div className="flex space-x-2 mt-4">
              <button onClick={handleSaveLesson} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
              <button onClick={() => setShowLessonModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========================
// ASSESSMENTS TAB
// ========================
function AssessmentsTab() {
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', assessment_type: 'quiz', time_limit_minutes: '', passing_score: 0, max_attempts: 1, category_id: '', is_published: false, questions: [] });
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  useEffect(() => { loadAssessments(); loadCategories(); }, []);

  const loadCategories = async () => {
    try {
      const res = await categoryApi.listAll();
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const loadAssessments = async () => {
    try {
      const res = await assessmentApi.getAssessments();
      setAssessments(res.data);
    } catch (error) {
      toast.error('Failed to load assessments');
    } finally { setIsLoading(false); }
  };

  const addQuestion = () => {
    setForm(prev => ({
      ...prev,
      questions: [...prev.questions, {
        question_type: 'mcq',
        question_text: '',
        options: [{ key: 'A', text: '' }, { key: 'B', text: '' }, { key: 'C', text: '' }, { key: 'D', text: '' }],
        correct_answer: '',
        points: 1,
        explanation: ''
      }]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setForm(prev => {
      const questions = [...prev.questions];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, questions };
    });
  };

  const updateOption = (qIndex, optIndex, text) => {
    setForm(prev => {
      const questions = [...prev.questions];
      const options = [...questions[qIndex].options];
      options[optIndex] = { ...options[optIndex], text };
      questions[qIndex] = { ...questions[qIndex], options };
      return { ...prev, questions };
    });
  };

  const removeQuestion = (index) => {
    setForm(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      await assessmentApi.createAssessment({
        ...form,
        time_limit_minutes: form.time_limit_minutes ? parseInt(form.time_limit_minutes) : null
      });
      toast.success('Assessment created');
      setShowModal(false);
      setForm({ title: '', description: '', assessment_type: 'quiz', time_limit_minutes: '', passing_score: 0, max_attempts: 1, category_id: '', is_published: false, questions: [] });
      loadAssessments();
    } catch (error) {
      toast.error('Failed to create assessment');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this assessment?')) return;
    try {
      await assessmentApi.deleteAssessment(id);
      toast.success('Assessment deleted');
      loadAssessments();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (isLoading) return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowModal(true)} className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <Plus className="w-4 h-4 mr-2" /> New Assessment
        </button>
      </div>

      {assessments.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No assessments yet.</p>
      ) : (
        <div className="space-y-2">
          {assessments.map(a => (
            <div key={a.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">{a.title}</h3>
                <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                  <span className="capitalize">{a.assessment_type}</span>
                  <span>{a.question_count} questions</span>
                  <span className={a.is_published ? 'text-green-600' : 'text-yellow-600'}>
                    {a.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button onClick={() => navigate(`/lms/assessments/${a.id}`)} className="p-1.5 text-gray-400 hover:text-primary-600"><Eye className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(a.id)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Assessment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">New Assessment</h3>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            <div className="space-y-3">
              <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Assessment title" />
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Description" />

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <select value={form.assessment_type} onChange={e => setForm({ ...form, assessment_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="quiz">Quiz</option>
                    <option value="assignment">Assignment</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Time Limit (min)</label>
                  <input type="number" value={form.time_limit_minutes} onChange={e => setForm({ ...form, time_limit_minutes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="No limit" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max Attempts</label>
                  <input type="number" value={form.max_attempts} onChange={e => setForm({ ...form, max_attempts: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Exam Category</label>
                  <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">-- Select Category --</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center space-x-1 pt-4">
                  <input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })} />
                  <span className="text-sm">Published</span>
                </label>
              </div>

              {/* Questions */}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">Questions ({form.questions.length})</h4>
                  <button onClick={addQuestion} className="text-sm text-primary-600 hover:text-primary-700 flex items-center">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Question
                  </button>
                </div>

                {form.questions.map((q, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500">Q{i + 1}</span>
                      <div className="flex items-center space-x-2">
                        <select value={q.question_type} onChange={e => updateQuestion(i, 'question_type', e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1">
                          <option value="mcq">MCQ</option>
                          <option value="subjective">Subjective</option>
                        </select>
                        <input type="number" value={q.points} onChange={e => updateQuestion(i, 'points', parseFloat(e.target.value) || 1)} className="w-16 text-xs border border-gray-300 rounded px-2 py-1" placeholder="Points" />
                        <button onClick={() => removeQuestion(i)} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>

                    <textarea value={q.question_text} onChange={e => updateQuestion(i, 'question_text', e.target.value)} rows={2} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mb-2" placeholder="Question text" />

                    {q.question_type === 'mcq' && (
                      <div className="space-y-1.5">
                        {q.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name={`correct_${i}`}
                              checked={q.correct_answer === opt.key}
                              onChange={() => updateQuestion(i, 'correct_answer', opt.key)}
                            />
                            <span className="text-xs font-medium text-gray-500 w-4">{opt.key}.</span>
                            <input
                              type="text"
                              value={opt.text}
                              onChange={e => updateOption(i, oi, e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                              placeholder={`Option ${opt.key}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    <input type="text" value={q.explanation || ''} onChange={e => updateQuestion(i, 'explanation', e.target.value)} className="w-full px-2 py-1 border border-gray-200 rounded text-xs mt-2" placeholder="Explanation (optional)" />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-2 mt-4">
              <button onClick={handleSave} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Create Assessment</button>
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========================
// LIVE SESSIONS TAB
// ========================
function LiveSessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', meeting_url: '', meeting_platform: 'zoom', scheduled_at: '', duration_minutes: 60 });

  useEffect(() => { loadSessions(); }, []);

  const loadSessions = async () => {
    try {
      const res = await lmsApi.getLiveSessions();
      setSessions(res.data);
    } catch (error) {
      toast.error('Failed to load sessions');
    } finally { setIsLoading(false); }
  };

  const handleSave = async () => {
    if (!form.title || !form.meeting_url || !form.scheduled_at) {
      toast.error('Title, meeting URL, and time are required');
      return;
    }
    try {
      await lmsApi.createLiveSession(form);
      toast.success('Live session created');
      setShowModal(false);
      setForm({ title: '', description: '', meeting_url: '', meeting_platform: 'zoom', scheduled_at: '', duration_minutes: 60 });
      loadSessions();
    } catch (error) {
      toast.error('Failed to create session');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Cancel this session?')) return;
    try {
      await lmsApi.deleteLiveSession(id);
      toast.success('Session cancelled');
      loadSessions();
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  if (isLoading) return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowModal(true)} className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <Plus className="w-4 h-4 mr-2" /> New Session
        </button>
      </div>

      {sessions.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No live sessions yet.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <div key={s.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">{s.title}</h3>
                <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                  <span>{new Date(s.scheduled_at).toLocaleString()}</span>
                  <span>{s.duration_minutes} min</span>
                  <span className="capitalize">{s.meeting_platform.replace('_', ' ')}</span>
                  <span className={`px-1.5 py-0.5 rounded ${
                    s.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                    s.status === 'completed' ? 'bg-green-100 text-green-700' :
                    s.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>{s.status}</span>
                </div>
              </div>
              {s.status === 'scheduled' && (
                <button onClick={() => handleDelete(s.id)} className="p-1.5 text-gray-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">New Live Session</h3>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Session title" />
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Description" />
              <input type="text" value={form.meeting_url} onChange={e => setForm({ ...form, meeting_url: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Meeting URL (Zoom/Meet link)" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
                  <select value={form.meeting_platform} onChange={e => setForm({ ...form, meeting_platform: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="zoom">Zoom</option>
                    <option value="google_meet">Google Meet</option>
                    <option value="teams">Teams</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Duration (min)</label>
                  <input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Scheduled Date & Time</label>
                <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex space-x-2 mt-4">
              <button onClick={handleSave} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Create Session</button>
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========================
// SUBMISSIONS TAB
// ========================
function SubmissionsTab() {
  const navigate = useNavigate();
  return (
    <div className="text-center py-8">
      <Upload className="w-12 h-12 mx-auto text-gray-300 mb-3" />
      <p className="text-gray-600 mb-4">Review and grade student submissions</p>
      <button
        onClick={() => navigate('/lms/submissions')}
        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
      >
        Open Submissions
      </button>
    </div>
  );
}

export default ManageLMS;
